import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";
import formOptions from "../../config/formOptions.json";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import ArchiveCompanyModal from "../../components/ArchiveCompanyModal";
import ArchiveCompanyAddressModal from "../../components/ArchiveCompanyAddressModal";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { graphqlEndpoint } from "../../config";
import { READ_COMPANY_BY_ID } from "../../graph/companies/queries";
import { UPDATE_COMPANY, ARCHIVE_COMPANY } from "../../graph/companies/mutations";
import { READ_COMPANY_ADDRESSES_BY_COMPANY_ID } from "../../graph/companyAddresses/queries";
import { ARCHIVE_COMPANY_ADDRESS } from "../../graph/companyAddresses/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const validationSchema = yup.object().shape({
  legalName: yup.string().trim().required("Legal name is required"),
  tradingName: yup.string().nullable(),
  industry: yup.string().nullable(),
  companySizeBand: yup.string().nullable(),
  websiteURL: yup.string().url("Enter a valid URL").nullable(),
  linkedInURL: yup.string().url("Enter a valid URL").nullable(),
  phone: yup.string().nullable(),
  email: yup.string().email("Enter a valid email").nullable(),
  status: yup.string().required("Status is required"),
});




const EditCompanyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();

  const [initialValues, setInitialValues] = useState(null);
  const [company, setCompany] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [archiveCompanyModalOpen, setArchiveCompanyModalOpen] = useState(false);
  const [archiveAddressModalOpen, setArchiveAddressModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  const redirectPath = location.state?.redirectPath || "/manageCompanies";

  const fetchCompanyAndAddresses = async () => {
    try {
      const [{ readCompanyByID }, { readCompanyAddressesByCompanyID }] =
        await Promise.all([
          client.request(READ_COMPANY_BY_ID, { id }),
          client.request(READ_COMPANY_ADDRESSES_BY_COMPANY_ID, {
            companyID: id,
          }),
        ]);

      if (!readCompanyByID) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Company not found",
        });
        navigate(redirectPath);
        return;
      }

      setCompany(readCompanyByID);

      setInitialValues({
        legalName: readCompanyByID.legalName || "",
        tradingName: readCompanyByID.tradingName || "",
        industry: readCompanyByID.industry || "",
        companySizeBand: readCompanyByID.companySizeBand || "",
        websiteURL: readCompanyByID.websiteURL || "",
        linkedInURL: readCompanyByID.linkedInURL || "",
        phone: readCompanyByID.phone || "",
        email: readCompanyByID.email || "",
        status: readCompanyByID.status || "PROSPECT",
        primaryContactID: readCompanyByID.primaryContactID || "",
      });

      setAddresses(readCompanyAddressesByCompanyID || []);
    } catch (err) {
      console.error("Error fetching company:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load company",
      });
    }
  };

  useEffect(() => {
    fetchCompanyAndAddresses();

    if (location.state?.refresh) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.state?.refresh]);

  const handleConfirmArchiveCompany = async () => {
    try {
      await client.request(ARCHIVE_COMPANY, { id: String(id) });

      setArchiveCompanyModalOpen(false);

      setSnackbar({
        open: true,
        severity: "success",
        message: "Company archived successfully",
      });

      navigate(redirectPath);
    } catch (err) {
      console.error("Archive failed:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to archive company",
      });
    }
  };

  const handleOpenArchiveAddressModal = (address) => {
    setSelectedAddress(address);
    setArchiveAddressModalOpen(true);
  };

  const handleCloseArchiveAddressModal = () => {
    setSelectedAddress(null);
    setArchiveAddressModalOpen(false);
  };

  const handleConfirmArchiveAddress = async () => {
    if (!selectedAddress?.id) return;

    try {
      await client.request(ARCHIVE_COMPANY_ADDRESS, {
        id: selectedAddress.id,
      });

      setAddresses((prev) =>
        prev.filter((address) => address.id !== selectedAddress.id)
      );

      handleCloseArchiveAddressModal();

      setSnackbar({
        open: true,
        severity: "success",
        message: "Address archived successfully",
      });
    } catch (err) {
      console.error("Archive address failed:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to archive address",
      });
    }
  };

  const handleFormSubmit = async (values) => {
    const cleanedValues = { ...values };

    Object.keys(cleanedValues).forEach((key) => {
      if (cleanedValues[key] === "") cleanedValues[key] = null;
    });

    cleanedValues.id = id;

    try {
      await client.request(UPDATE_COMPANY, {
        input: cleanedValues,
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Company updated successfully",
      });

      navigate(redirectPath, { state: { refresh: true } });
    } catch (err) {
      console.error("Update failed:", err?.response?.errors || err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update company",
      });
    }
  };

  if (!initialValues) return <div>Loading...</div>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="EDIT COMPANY"
          subtitle={`Edit company: ${
            initialValues.tradingName || initialValues.legalName
          }`}
        />

        <Tooltip title="Archive">
          <ActionButton
            tone="danger"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => setArchiveCompanyModalOpen(true)}
          >
            Archive Company
          </ActionButton>
        </Tooltip>
      </Box>

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                label="Legal Name"
                name="legalName"
                value={values.legalName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.legalName && !!errors.legalName}
                helperText={touched.legalName && errors.legalName}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Trading Name"
                name="tradingName"
                value={values.tradingName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.tradingName && !!errors.tradingName}
                helperText={touched.tradingName && errors.tradingName}
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Industry</InputLabel>
                <Select
                  name="industry"
                  value={values.industry}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Industry"
                >
                  <MenuItem value="">None</MenuItem>
                  {formOptions.industryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Company Size</InputLabel>
                <Select
                  name="companySizeBand"
                  value={values.companySizeBand}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Company Size"
                >
                  <MenuItem value="">None</MenuItem>
                  {formOptions.companySizeBandOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Status"
                >
                  {formOptions.companyStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Website URL"
                name="websiteURL"
                value={values.websiteURL}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.websiteURL && !!errors.websiteURL}
                helperText={touched.websiteURL && errors.websiteURL}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="LinkedIn URL"
                name="linkedInURL"
                value={values.linkedInURL}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.linkedInURL && !!errors.linkedInURL}
                helperText={touched.linkedInURL && errors.linkedInURL}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Phone"
                name="phone"
                value={values.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.phone && !!errors.phone}
                helperText={touched.phone && errors.phone}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Email"
                name="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                sx={{ gridColumn: "span 2" }}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <ActionButton type="submit" tone="success">
                Save Changes
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>

      <Box mt="40px">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Header
            title="ADDRESSES"
            subtitle="Addresses linked to this company"
          />

          <ActionButton
            tone="teal"
            size="small"
            onClick={() =>
              navigate("/createCompanyAddress", {
                state: {
                  companyID: id,
                  redirectPath: `/companies/edit/${id}`,
                },
              })
            }
          >
            + Address
          </ActionButton>
        </Box>

        {addresses.length === 0 ? (
          <Typography mt={2} color="text.secondary">
            No addresses have been added for this company yet.
          </Typography>
        ) : (
          <DataGrid
            autoHeight
            rows={addresses}
            columns={[
              { field: "label", headerName: "Label", flex: 0.8 },
              {
                field: "addressLine1",
                headerName: "Address Line 1",
                flex: 1.4,
              },
              { field: "town", headerName: "Town", flex: 0.9 },
              { field: "county", headerName: "County", flex: 0.9 },
              { field: "postcode", headerName: "Postcode", flex: 0.8 },
              { field: "country", headerName: "Country", flex: 1 },
              {
                field: "isPrimary",
                headerName: "Primary",
                flex: 0.7,
                renderCell: ({ value }) => (value ? "Yes" : "No"),
              },
              {
                field: "actions",
                headerName: "Actions",
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: ({ row }) => (
                  <Box display="flex" gap="10px">
                    <ActionButton
                      size="small"
                      tone="teal"
                      onClick={() =>
                        navigate(`/companyAddresses/edit/${row.id}`, {
                          state: {
                            redirectPath: `/companies/edit/${id}`,
                          },
                        })
                      }
                    >
                      Edit
                    </ActionButton>

                    <ActionButton
                      size="small"
                      tone="danger"
                      onClick={() => handleOpenArchiveAddressModal(row)}
                    >
                      Archive
                    </ActionButton>
                  </Box>
                ),
              },
            ]}
          />
        )}
      </Box>

      <ArchiveCompanyModal
        open={archiveCompanyModalOpen}
        onClose={() => setArchiveCompanyModalOpen(false)}
        onConfirm={handleConfirmArchiveCompany}
        company={company}
      />

      <ArchiveCompanyAddressModal
        open={archiveAddressModalOpen}
        onClose={handleCloseArchiveAddressModal}
        onConfirm={handleConfirmArchiveAddress}
        address={selectedAddress}
      />
    </Box>
  );
};

export default EditCompanyForm;